import {Box, Button, Chip, Divider, Menu, MenuItem, Slide, Typography, useMediaQuery, useTheme} from '@mui/material'
import React, {useCallback, useEffect, useRef, useState, Fragment} from 'react'
import Chevron from 'assets/images/normal_chevron-booking.svg'
import Chevron1 from 'assets/images/selected_chevron-booking.svg'
import {useSelector} from 'react-redux'
import {cpsUrl} from 'config/base'
import {ENDPOINT} from 'config/path'
import {useNavigate} from 'react-router-dom'
import {type RootState} from 'redux/store'
import _ from 'lodash'

export interface TMenuItem {
    title: string
    pathname?: string
    authPrivilege: string
    subMenus?: TMenuItem[]
    badge?: string
    subTitle?: string
    divider?: boolean
    active?: boolean
    disable?: boolean
}
const DropdownMenuItem = ({
    menuItem,
    menuShowingDropdown,
    setMenuShowingDropdown
}: {
    menuItem: TMenuItem
    menuShowingDropdown: string
    setMenuShowingDropdown: (menuTitle: string) => void
}) => {
    const navigate = useNavigate()
    const {title, subMenus} = menuItem
    const buttonRef = useRef<null | HTMLButtonElement>(null)

    const showSubMenu = useCallback(() => {
        const focusedElement = document.activeElement as HTMLInputElement
        if (focusedElement) {
            focusedElement.blur()
        }
        setMenuShowingDropdown(menuItem.title)
    }, [menuItem.title, setMenuShowingDropdown])

    const closeSubMenu = useCallback(() => {
        setMenuShowingDropdown('')
    }, [setMenuShowingDropdown])

    const navigatePage = (url: string, e: any = {ctrlKey: false}) => {
        if (e.ctrlKey) {
            window.open(`${cpsUrl}${url.includes('.jsp') ? '' : '/cpsreact'}${url}`, '_blank')
        } else if (url.includes('.jsp')) {
            window.location.href = `${cpsUrl}${url}`
        } else {
            navigate(url)
        }
        closeSubMenu()
    }

    useEffect(() => {
        window.addEventListener('scroll', () => closeSubMenu())
        return () => {
            window.removeEventListener('scroll', () => closeSubMenu())
        }
    }, [])

    const subMenusNodes = subMenus?.map((subMenuItem: TMenuItem) => (
        <MenuItem
            disabled={subMenuItem.disable}
            onClick={(e) => {
                navigatePage(_.get(subMenuItem, 'pathname', `${process.env.PUBLIC_URL}${ENDPOINT.homepage}`), e)
            }}
            key={subMenuItem.title}
            sx={{
                padding: '8px 16px',
                '&:hover': {backgroundColor: 'white', color: '#356eab'}
            }}
            className={subMenuItem.subTitle ? 'SubMenuItem' : ''}
        >
            {subMenuItem.title}
            {subMenuItem.badge && (
                <Chip className='MenuItem-Chip' label={subMenuItem.badge} size='small' sx={{fontSize: '10px'}} />
            )}
            {subMenuItem.subTitle && <Typography className='MenuItem-SubTitle'>{subMenuItem.subTitle}</Typography>}
            {subMenuItem.divider && <Divider className='MenuItem-Divider' />}
        </MenuItem>
    ))

    const theme = useTheme()
    return (
        <>
            <Box
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        if (_.get(menuItem, 'subMenus', []).length > 0) showSubMenu()
                        else if (menuItem.pathname) navigatePage(menuItem.pathname)
                    }
                }}
                className={_.get(menuItem, 'disable', false) ? 'DisabledBox' : ''}
                id={`menuItem-${title}`}
                data-testid={`menuItem-${title}`}
                sx={{
                    zIndex: theme.zIndex.modal + 1,
                    fontWeight: '500',
                    color: '#192d52',
                    px: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    fontFamily: '"Roboto-Medium", "Roboto Medium", "Roboto", sans-serif',
                    textAlign: 'left',
                    lineHeight: 'normal',
                    letterSpacing: '0.6px',
                    height: '65px',
                    '&:hover': {boxSizing: 'border-box', color: '#356eab', cursor: 'pointer'}
                }}
                ref={buttonRef}
                onClick={(e) => {
                    if (_.get(menuItem, 'subMenus', []).length === 0) {
                        navigatePage(_.get(menuItem, 'pathname', `${process.env.PUBLIC_URL}${ENDPOINT.homepage}`), e)
                    }
                }}
                onMouseLeave={() => {
                    setMenuShowingDropdown('')
                }}
                onMouseEnter={() => {
                    if (_.get(menuItem, 'subMenus', []).length > 0) {
                        showSubMenu()
                    }
                }}
            >
                {title}
                {_.get(menuItem, 'subMenus', []).length > 0 && (
                    <Box id='ExpandIcon'>
                        {menuShowingDropdown === menuItem.title ? (
                            <img src={Chevron1} className='ExpandMore' alt='ExpandMoreIcon' />
                        ) : (
                            <img src={Chevron} className='ExpandLess' alt='ExpandLessIcon' />
                        )}
                    </Box>
                )}
            </Box>
            <Menu
                className='DropdownMenu'
                PaperProps={{
                    onMouseEnter: () => {
                        showSubMenu()
                    },
                    onMouseLeave: () => {
                        closeSubMenu()
                    },
                    style: {
                        borderLeft: '5px solid',
                        WebkitBorderImage: '-webkit-linear-gradient(bottom,  #32936f, #a1e8af) 1 100%',
                        padding: 0,
                        margin: 0
                    }
                }}
                anchorEl={buttonRef.current}
                open={menuShowingDropdown === menuItem.title}
                onClose={closeSubMenu}
                disableAutoFocusItem
                disableScrollLock
            >
                {subMenusNodes}
            </Menu>
        </>
    )
}

const HamMenuItem = ({
    menuItem,
    menuShowingDropdown,
    setMenuOpen,
    setMenuShowingDropdown
}: {
    menuItem: TMenuItem
    menuShowingDropdown: string
    setMenuOpen: React.Dispatch<React.SetStateAction<boolean>>
    setMenuShowingDropdown: (menuTitle: string) => void
}) => {
    const navigate = useNavigate()
    const [isHovered, setIsHovered] = useState(false)
    const {title, subMenus} = menuItem
    const buttonRef = useRef<null | HTMLButtonElement>(null)
    const userBranchId = useSelector((state: RootState) => state?.login?.loginDetails?.securityParams?.branchID)
    const showSubMenu = useCallback(() => {
        setMenuShowingDropdown(menuItem.title)
        window.scrollTo(0, 0)
    }, [menuItem.title, setMenuShowingDropdown])

    const closeSubMenu = useCallback(() => {
        setMenuShowingDropdown('')
    }, [setMenuShowingDropdown])

    const handleMouseEnter = () => {
        setIsHovered(true)
    }

    const handleMouseLeave = () => {
        setIsHovered(false)
    }

    const navigatePage = (url: string) => {
        if (url.includes('.jsp')) window.location.href = `${cpsUrl}${url}`
        else navigate(url)
        setMenuOpen((prevState: any) => !prevState)
    }

    const subMenusNodes = subMenus?.map((subMenuItem) => (
        <MenuItem
            disabled={_.get(subMenuItem, 'disable', false)}
            onClick={() => {
                navigatePage(_.get(subMenuItem, 'pathname', `${process.env.PUBLIC_URL}${ENDPOINT.homepage}`))
            }}
            key={subMenuItem.title}
            sx={{
                fontSize: '16px',
                fontWeight: '400',
                padding: '8px 16px',
                '&:hover': {backgroundColor: 'white', color: '#356eab'}
            }}
            className='class_submenunode'
        >
            {subMenuItem.title}
        </MenuItem>
    ))

    const theme = useTheme()
    return (
        <>
            <Box
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        if (menuItem.subMenus) showSubMenu()
                        else if (menuItem.pathname) navigatePage(menuItem.pathname)
                    }
                }}
                className={_.get(menuItem, 'disable', false) ? 'DisabledBox' : ''}
                id={`menuItem-${title}`}
                data-testid={`menuItem-${title}`}
                sx={{
                    zIndex: theme.zIndex.modal + 1,
                    fontWeight: '500',
                    color: '#2C2C2C',
                    px: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    display:
                        !(String(userBranchId) === '10000' || String(userBranchId) === '10010') &&
                        (title === 'e-Docs' || title === 'e-Finance')
                            ? 'none'
                            : 'flex',
                    fontFamily: '"Roboto-Medium", "Roboto Medium", "Roboto", sans-serif',
                    textAlign: 'left',
                    lineHeight: 'normal',
                    letterSpacing: '0.6px',
                    height: '65px',
                    '&:hover': {boxSizing: 'border-box', color: '#356eab', cursor: 'pointer'},
                    width: 'auto',
                    placeContent: 'flex-start',
                    paddingLeft: '32px'
                }}
                ref={buttonRef}
                onClick={() => {
                    if (_.get(menuItem, 'subMenus', []).length === 0) {
                        navigatePage(_.get(menuItem, 'pathname', `${process.env.PUBLIC_URL}${ENDPOINT.homepage}`))
                    } else {
                        showSubMenu()
                    }
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Box sx={{fontSize: '16px', fontWeight: '500'}} className='listItems'>
                    {title}
                    {_.get(menuItem, 'subMenus', []).length > 0 && (
                        <Box id='ExpandIcon' sx={{alignItems: 'flex-end'}}>
                            <img src={isHovered ? Chevron1 : Chevron} className='listExpandMore' alt='ExpandLessIcon' />
                        </Box>
                    )}
                </Box>
            </Box>
            <Menu
                className='ListDropdownMenu'
                sx={{
                    '& .MuiBackdrop-root': {
                        top: '82px'
                    }
                }}
                anchorEl={buttonRef.current}
                open={menuShowingDropdown === menuItem.title}
                onClose={closeSubMenu}
                slotProps={{paper: {className: 'paper'}}}
            >
                <Slide
                    className='slideContainer'
                    direction='left'
                    in={menuShowingDropdown === menuItem.title}
                    timeout={{enter: 700, exit: 100}}
                    mountOnEnter
                    unmountOnExit
                    easing='cubic-bezier(0.22, 1, 0.36, 1)'
                >
                    <Box>
                        <Box my={1} className='box1' onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                            <Button
                                variant='text'
                                onClick={closeSubMenu}
                                className='subMenuBtn'
                                sx={{
                                    color: 'black',
                                    width: '100%',
                                    placeContent: 'flex-start',
                                    paddingLeft: '15px'
                                }}
                            >
                                <img
                                    src={isHovered ? Chevron1 : Chevron}
                                    className='leftExpandMore'
                                    alt='ExpandMoreIcon'
                                />
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        textTransform: 'capitalize',
                                        paddingLeft: '5px',
                                        '&:hover': {
                                            color: '#356eab',
                                            backgroundColor: '#ffffff'
                                        }
                                    }}
                                >
                                    Main Menu
                                </Typography>
                            </Button>
                        </Box>
                        <Box my={3} className='box2'>
                            <Typography sx={{color: 'Black', fontSize: '16px', fontWeight: '700', paddingLeft: '15px'}}>
                                {menuShowingDropdown}
                            </Typography>
                        </Box>
                        <Box sx={{color: 'Black', fontSize: '16px'}} className='box3'>
                            {subMenusNodes}
                        </Box>
                    </Box>
                </Slide>
            </Menu>
            <Divider
                className='hamdivider'
                sx={{
                    display:
                        !(String(userBranchId) === '10000' || String(userBranchId) === '10010') &&
                        (title === 'e-Docs' || title === 'e-Finance')
                            ? 'none'
                            : 'flex'
                }}
            />
        </>
    )
}
export const TopMenu = ({setMenuOpen}: {setMenuOpen?: React.Dispatch<React.SetStateAction<boolean>>}) => {
    const theme = useTheme()
    const belowMd = useMediaQuery(theme.breakpoints.down('md'))
    const [menuShowingDropdown, setMenuShowingDropdown] = useState('')
    const menuList = useSelector((state: any) => state.login.menus ?? [])

    const handleMenuShowingDropdownChange = useCallback((menuTitle: string) => {
        setMenuShowingDropdown(menuTitle)
    }, [])

    if (belowMd) {
        return (
            <Fragment>
                {menuList?.map((menuGroup) => (
                    <HamMenuItem
                        key={menuGroup.title}
                        menuItem={menuGroup}
                        menuShowingDropdown={menuShowingDropdown}
                        setMenuOpen={setMenuOpen ?? (() => {})}
                        setMenuShowingDropdown={handleMenuShowingDropdownChange}
                    />
                ))}
            </Fragment>
        )
    } else {
        return (
            <Fragment>
                {menuList?.map((menuGroup) => (
                    <DropdownMenuItem
                        key={menuGroup.title}
                        menuItem={menuGroup}
                        menuShowingDropdown={menuShowingDropdown}
                        setMenuShowingDropdown={handleMenuShowingDropdownChange}
                    />
                ))}
            </Fragment>
        )
    }
}
